import gql from 'graphql-tag';
import { useQuery, provideApolloClient } from '@vue/apollo-composable';
import { watch, ref } from 'vue';
import useAlerts from './useAlerts';
import useCurrentUser from './useCurrentUser';
import apolloClient from './useApolloClient';
import sound from '@/assets/reminder-alert.wav';

const { addAlert } = useAlerts();
let note = new Audio(sound);

const GET_USER_REMINDERS = gql`
  query UserRemindersQuery($assignedid: ID, $completed: Boolean) {
    userReminders(assignedid: $assignedid, completed: $completed) {
      COMPANYID
      COMPLETED
      CONTACTID
      DATE
      REMINDERID
      TEXT
      TYPE
      ASSIGNEDID
      company {
        NAME
        COMPANYID
      }
      contact {
        FIRSTNAME
        LASTNAME
        CONTACTID
      }
      lead {
        LEADID
        company {
          NAME
          COMPANYID
        }
        contact {
          FIRSTNAME
          LASTNAME
          CONTACTID
        }
      }
    }
  }
`;

const GET_ENTITY_REMINDERS = gql`
  query EntityRemindersQuery($companyid: ID, $contactid: ID, $leadid: ID) {
    entityReminders(companyid: $companyid, contactid: $contactid, leadid: $leadid) {
      COMPANYID
      COMPLETED
      CONTACTID
      DATE
      REMINDERID
      TEXT
      TYPE
      ASSIGNEDID
      company {
        NAME
        COMPANYID
      }
      contact {
        FIRSTNAME
        LASTNAME
        CONTACTID
      }
      lead {
        LEADID
        company {
          NAME
          COMPANYID
        }
        contact {
          FIRSTNAME
          LASTNAME
          CONTACTID
        }
      }
    }
  }
`;

const refetchReminders = ref(null);
const reminders = ref([]);

// Function to check if a reminder's date is in the future
function isFutureDate(timestamp) {
  try {
    // Ensure timestamp is a number
    const parsedTimestamp = parseInt(timestamp);
    if (isNaN(parsedTimestamp)) {
      console.error('Invalid timestamp:', timestamp);
      return false; // Default to showing reminder if timestamp is invalid
    }

    const reminderDate = new Date(parsedTimestamp);
    const today = new Date();
    // Set both dates to start of day for comparison
    reminderDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return reminderDate > today;
  } catch (error) {
    console.error('Error processing reminder date:', error);
    return false; // Default to showing reminder if there's an error
  }
}

function getUserReminders() {
  const { user } = useCurrentUser();
  let newReminders = [];
  let oldReminders = [];

  const { result, error, refetch } = useQuery(GET_USER_REMINDERS, () => ({
    assignedid: user.userid,
    completed: false,
  }));

  refetchReminders.value = refetch;

  watch(
    user,
    () => {
      refetch();
    },
    { deep: true }
  );

  setInterval(() => {
    refetch();
  }, 5 * 60 * 60 * 1000);

  watch(result, () => {
    newReminders = result.value?.userReminders || [];

    // Filter out future reminders
    newReminders = newReminders.filter((reminder) => !isFutureDate(reminder.DATE));

    const addedReminders = newReminders.filter((newReminder) => !oldReminders.some((oldReminder) => oldReminder.REMINDERID === newReminder.REMINDERID) && !newReminder.COMPLETED);

    if (addedReminders.length > 0) {
      note.play().catch((e) => {
        window.addEventListener(
          'click',
          () => {
            note.play();
          },
          { once: true }
        );
      });
    }

    oldReminders = newReminders;
    reminders.value = newReminders;
  });

  watch(error, () => {
    if (error.value) {
      console.error('Error fetching reminders:', error.value);
      addAlert('Error fetching reminders', 'high', 4);
    }
  });
}

function getEntityReminders() {
  return function (params) {
    provideApolloClient(apolloClient);

    const { result, loading, error, refetch } = useQuery(GET_ENTITY_REMINDERS, params, { fetchPolicy: 'no-cache' });

    watch(error, () => {
      if (error.value) {
        console.error('Error fetching entity reminders:', error.value);
        addAlert('Error fetching reminders', 'high', 5);
      }
    });

    return {
      result,
      loading,
      error,
      refetch,
    };
  };
}

export default {
  getUserReminders,
  getEntityReminders,
  refetchReminders,
  reminders,
};
